import React from "react";
import { observer } from "mobx-react";
import SparkleIcon from "Components/common/SparkleIcon";
import BlockRenderer from "./BlockRenderer";
import ComposingHint from "./ComposingHint";
import MessageActions from "./MessageActions";
import ActivityGroup from "./blocks/ActivityGroup";
import { BLOCK } from "../agent/agentContract";

/**
 * Group a turn's blocks for rendering: consecutive progress blocks become one
 * activity rail, and a table card repeated with the same title in the same
 * turn (the model re-ran the search) keeps only the latest.
 */
const segment = blocks => {
  const lastTableByTitle = {};
  blocks.forEach(b => {
    if (b.type === BLOCK.TABLE_REF && !b.hidden) lastTableByTitle[b.title] = b.id;
  });
  const out = [];
  blocks.forEach(block => {
    if (block.type === BLOCK.PROGRESS) {
      const prev = out[out.length - 1];
      if (prev && prev.kind === "activity") prev.steps.push(block);
      else out.push({ kind: "activity", id: `act_${block.id}`, steps: [block] });
      return;
    }
    if (block.type === BLOCK.TABLE_REF && lastTableByTitle[block.title] !== block.id) return;
    out.push({ kind: "block", id: block.id, block });
  });
  return out;
};

/**
 * True once something has rendered but the closing text has not arrived yet.
 * A text block that is still empty counts as "not arrived".
 */
const awaitingProse = message => {
  const blocks = message.blocks || [];
  if (!blocks.length) return false;
  const last = blocks[blocks.length - 1];
  // A running progress checklist already says what is happening; a second
  // rotating line under it reads as two loaders for one job.
  if (last.type === "progress" && last.status === "running") return false;
  return last.type !== "text" || !(last.markdown || "").trim();
};

// continued: this assistant message follows another assistant message, so it
// hangs under the same avatar instead of starting a second speaker.
// turnBlocks: every block in the run of assistant messages this one belongs
// to, so the rail colour on the first message reflects the whole turn (the
// question usually lands in a later, "continued" message).
const MessageRow = observer(({ message, continued = false, endOfTurn = false, prevUserText = "", isLast = false, turnBlocks = null, superseded = false }) => {
  if (message.role === "user") {
    return (
      <div className="copilotMsg copilotMsg--user">
        <div className="copilotMsg__userCol">
          <div
            className="copilotMsg__bubble"
            style={message.queued ? { opacity: 0.6 } : undefined}
          >
            {message.text}
          </div>
          {message.queued && (
            <div className="copilotMsg__queuedHint">
              Queued — sending after the current response
            </div>
          )}
        </div>
      </div>
    );
  }

  const blocks = message.blocks || [];
  const lastId = blocks.length ? blocks[blocks.length - 1].id : null;
  const items = segment(blocks);
  // Card anatomy (full page): a leading activity rail becomes the muted
  // "log" strip on top; everything after it is the body; actions sit in
  // the card footer. The drawer reuses the same markup unstyled.
  const leadingLog = items.length && items[0].kind === "activity" ? items[0] : null;
  const bodyItems = leadingLog ? items.slice(1) : items;
  const all = turnBlocks || blocks;
  const failed = all.some(
    b => (b.type === BLOCK.ERROR_CTA && b.severity !== "warn") || (b.type === BLOCK.PROGRESS && b.status === "failed")
  );
  const warned = !failed && all.some(b => b.type === BLOCK.ERROR_CTA && b.severity === "warn");
  // Anything the user still has to answer: a form, a confirmation, or a
  // choice list with nothing picked yet.
  const openAsk = b =>
    (b.type === BLOCK.QUESTION_FORM && !b.done) ||
    // A confirmation is answered once it carries a resolution (it never sets done).
    (b.type === BLOCK.CONFIRMATION && !b.done && !b.resolution) ||
    (b.type === BLOCK.QUICK_REPLIES && !b.selectedValue);
  const asking = !failed && all.some(openAsk);
  const done = !message.streaming && all.some(b => b.type === BLOCK.PROGRESS && b.status === "done");
  const tone = failed ? " copilotMsg--failed" : warned ? " copilotMsg--warn" : asking ? " copilotMsg--asking" : done ? " copilotMsg--done" : "";
  const empty = message.streaming && !blocks.length;
  const renderItem = item =>
    item.kind === "activity" ? (
      <ActivityGroup key={item.id} steps={item.steps} turnStreaming={message.streaming} />
    ) : (
      <BlockRenderer
        key={item.id}
        block={item.block}
        isStreaming={message.streaming && item.id === lastId}
        superseded={superseded}
      />
    );
  // Status band (ledger): the outcome in one line before the detail. Only
  // when the turn carries a state the reader must not miss.
  const errorCta = blocks.find(b => b.type === BLOCK.ERROR_CTA);
  // No "waiting on your pick" band: the question right there already says it.
  const band = !failed && warned ? { cls: "warn", text: errorCta.title || "Heads up" } : null;
  const toolCount = blocks.filter(b => b.type === BLOCK.PROGRESS).length;
  return (
    <div className={`copilotMsg copilotMsg--assistant${continued ? " copilotMsg--continued" : ""}${tone}`}>
      <div className="copilotMsg__avatar">
        <SparkleIcon />
      </div>
      <div className="copilotMsg__content">
        <div className={`copilotTurn${empty ? " copilotTurn--empty" : ""}${leadingLog ? " copilotTurn--ledger" : ""}`}>
          {leadingLog && (
            <div className="copilotTurn__log">
              <div className="copilotTurn__eyebrow">What I did</div>
              {renderItem(leadingLog)}
              {(toolCount > 1 || message.streaming) && (
                <div className="copilotTurn__logFoot">
                  {toolCount} {toolCount === 1 ? "step" : "steps"}
                  {message.streaming ? " · running" : ""}
                </div>
              )}
            </div>
          )}
          <div className="copilotTurn__main">
            {band && (
              <div className={`copilotTurn__band copilotTurn__band--${band.cls}`}>
                <span className="copilotTurn__bandIcon">⚠</span>
                {band.text}
              </div>
            )}
            {(bodyItems.length > 0 || message.streaming) && (
              <div className="copilotTurn__body">
                {bodyItems.map(renderItem)}
                {empty && (
                  <div className="copilotMsg__pending">
                    <span className="copilotDot" />
                    <span className="copilotDot" />
                    <span className="copilotDot" />
                  </div>
                )}
                {/* Blocks have landed but the answer is still being written — without
                    this the turn looks finished and silent. */}
                {message.streaming && awaitingProse(message) && <ComposingHint />}
              </div>
            )}
            {endOfTurn && (
              <div className="copilotTurn__foot">
                <MessageActions message={message} prevUserText={prevUserText} isLast={isLast} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default MessageRow;
